import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import GroupAddOutlinedIcon from "@mui/icons-material/GroupAddOutlined";
import { getUsuariosAsignados, quitarUsuarioProyecto } from "../usuariosProyectosApi";
import {
  filterUsuariosAsignados,
  getProyectoId,
  getUsuariosProyectosErrorMessage,
  getUsuarioId,
} from "../usuariosProyectosUtils";
import AsignarUsuariosDialog from "./AsignarUsuariosDialog";
import QuitarUsuarioDialog from "./QuitarUsuarioDialog";
import UsuariosProyectoTable from "./UsuariosProyectoTable";

const UsuariosProyectoManager = ({ project }) => {
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [isLoadingAssigned, setIsLoadingAssigned] = useState(false);
  const [assignedError, setAssignedError] = useState("");
  const [assignOpen, setAssignOpen] = useState(false);
  const [userToRemove, setUserToRemove] = useState(null);
  const [isUnassigning, setIsUnassigning] = useState(false);
  const [unassignError, setUnassignError] = useState("");
  const [snackbar, setSnackbar] = useState("");
  const assignedSequence = useRef(0);
  const projectId = getProyectoId(project);

  const loadAssigned = useCallback(async () => {
    if (!projectId) return;
    const sequence = ++assignedSequence.current;
    setIsLoadingAssigned(true);
    setAssignedError("");

    try {
      const assignedUsers = await getUsuariosAsignados(projectId);
      if (sequence === assignedSequence.current) setUsers(assignedUsers);
    } catch (error) {
      if (sequence === assignedSequence.current) {
        setUsers([]);
        setAssignedError(getUsuariosProyectosErrorMessage(error, "assigned"));
      }
    } finally {
      if (sequence === assignedSequence.current) setIsLoadingAssigned(false);
    }
  }, [projectId]);

  useEffect(() => {
    setUsers([]);
    setSearch("");
    setAssignOpen(false);
    setUserToRemove(null);
    loadAssigned();
    return () => {
      assignedSequence.current += 1;
    };
  }, [loadAssigned]);

  const visibleUsers = useMemo(
    () => filterUsuariosAsignados(users, search),
    [search, users]
  );

  const handleAssigned = async ({ successes, failures }) => {
    await loadAssigned();
    if (successes.length > 0) {
      setSnackbar(
        successes.length === 1
          ? "Usuario asignado correctamente."
          : `${successes.length} usuarios asignados correctamente.`
      );
    }
    if (failures.length === 0) setAssignOpen(false);
  };

  const handleUnassign = async () => {
    if (!userToRemove || !projectId) return;
    setIsUnassigning(true);
    setUnassignError("");
    try {
      await quitarUsuarioProyecto(projectId, getUsuarioId(userToRemove));
      setUserToRemove(null);
      setSnackbar("Usuario retirado del proyecto.");
      await loadAssigned();
    } catch (error) {
      setUnassignError(getUsuariosProyectosErrorMessage(error, "unassign"));
    } finally {
      setIsUnassigning(false);
    }
  };

  return (
    <Box>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1.5}
        justifyContent="space-between"
        alignItems={{ xs: "stretch", sm: "center" }}
        sx={{ mb: 2 }}
      >
        <TextField
          label="Buscar usuario asignado"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          size="small"
          sx={{ width: { xs: "100%", sm: 360 } }}
        />
        <Button
          variant="contained"
          startIcon={<GroupAddOutlinedIcon />}
          onClick={() => setAssignOpen(true)}
          sx={{
            bgcolor: "teal",
            color: "white",
            "&:hover": { bgcolor: "teal" },
          }}
        >
          Asignar usuarios
        </Button>
      </Stack>

      {assignedError && (
        <Alert severity="error" action={<Button color="inherit" onClick={loadAssigned}>Reintentar</Button>} sx={{ mb: 2 }}>
          {assignedError}
        </Alert>
      )}

      {isLoadingAssigned ? (
        <Box minHeight={180} display="flex" alignItems="center" justifyContent="center">
          <CircularProgress size={30} />
        </Box>
      ) : (
        <>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {visibleUsers.length} usuario{visibleUsers.length === 1 ? "" : "s"} visible{visibleUsers.length === 1 ? "" : "s"}
          </Typography>
          <UsuariosProyectoTable rows={visibleUsers} onRemove={setUserToRemove} />
        </>
      )}

      <AsignarUsuariosDialog
        open={assignOpen}
        project={project}
        onClose={() => setAssignOpen(false)}
        onAssigned={handleAssigned}
      />
      <QuitarUsuarioDialog
        open={Boolean(userToRemove)}
        project={project}
        user={userToRemove}
        loading={isUnassigning}
        error={unassignError}
        onClose={() => {
          setUserToRemove(null);
          setUnassignError("");
        }}
        onConfirm={handleUnassign}
      />
      <Snackbar
        open={Boolean(snackbar)}
        autoHideDuration={4000}
        onClose={() => setSnackbar("")}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity="success" variant="filled" onClose={() => setSnackbar("")}>
          {snackbar}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default UsuariosProyectoManager;
